import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import ValuationResultsSupervisor from "./ValuationResultsSupervisor";
import { useValuation } from "../../context/ValuationContext";
import { useLiveFormData } from "@/hooks/useLiveFormData";
import {
  MessageSquare,
  Send,
  Clock,
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  Edit,
  Loader2,
  Check,
  Trash2,
  User,
  AlertTriangle,
  Info,
  Maximize2,
  Minimize2,
  Eye,
  Search,
  Filter,
} from "lucide-react";

// ── TYPES ────────────────────────────────────────────────────────────────

interface ValuationSidebarProps {
  property?: any;
  mode?: "collector" | "supervisor";
}

interface Comment {
  id: string;
  message: string | null;
  commentType: string;
  section: string;
  isResolved: boolean;
  resolvedAt: string | null;
  authorId: string;
  authorRole: string;
  createdAt: string;
  updatedAt: string;
  authorUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    profileImageUrl: string | null;
  } | null;
}

interface FeedbackMessage {
  id: string;
  message: string;
  priority: "P1" | "P2" | "P3";
  sender: string;
  senderRole: "supervisor" | "collector";
  timestamp: string;
  isRead: boolean;
  propertyId: string;
  collectorId?: string;
  collectorName?: string;
}

// ── CONFIG ───────────────────────────────────────────────────────────────

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";
const token = localStorage.getItem("valuation_token");

// ── COMPONENT ────────────────────────────────────────────────────────────

export default function ValuationSidebarSupervisor({
  property,
  mode = "collector",
}: ValuationSidebarProps) {
  const isSupervisor = mode === "supervisor";
  const { toast } = useToast();
  const { formatCurrency } = useValuation();
  const { combinedData } = useLiveFormData();

  // Currency detection based on property country
  const isUganda =
    !!property?.location?.region ||
    property?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  const formatLocalCurrency = (value: number) => {
    if (!value && value !== 0) return `${currency} 0`;
    return `${currency} ${Number(value).toLocaleString()}`;
  };

  // ── STATE ───────────────────────────────────────────────────────────────

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showQuickCommentModal, setShowQuickCommentModal] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    [],
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState<string | null>(
    null,
  );

  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackPriority, setFeedbackPriority] = useState<"P1" | "P2" | "P3">(
    "P1",
  );
  const [quickComment, setQuickComment] = useState("");
  const [commentSection, setCommentSection] = useState("valuation");

  // New states for enhanced comments view
  const [expandedComments, setExpandedComments] = useState<Set<string>>(
    new Set(),
  );
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [commentSearch, setCommentSearch] = useState("");
  const [commentSectionFilter, setCommentSectionFilter] = useState("all");
  const [commentStatusFilter, setCommentStatusFilter] = useState<
    "all" | "resolved" | "unresolved"
  >("all");
  const [hoveredComment, setHoveredComment] = useState<string | null>(null);

  // ── DATA FETCHING ───────────────────────────────────────────────────────

  const fetchComments = async () => {
    if (!property?.id) return;

    try {
      setIsLoadingComments(true);
      const response = await fetch(
        `${API_BASE_URL}/comments/properties/${property.id}?limit=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to get comments");
      }

      setComments(result.data.comments || []);
    } catch (error) {
      console.error("Comments fetch error:", error);
      // toast({
      //   title: "Error",
      //   description: "Failed to load comments",
      //   variant: "destructive",
      // });
    } finally {
      setIsLoadingComments(false);
    }
  };

  const fetchFeedback = async () => {
    if (!property?.id) return;

    // try {
    //   const response = await fetch(
    //     `${API_BASE_URL}/feedback?propertyId=${property.id}`,
    //     {
    //       headers: { Authorization: `Bearer ${token}` },
    //     },
    //   );

    //   if (!response.ok) throw new Error("Failed to fetch feedback");

    //   const data = await response.json();
    //   setFeedbackMessages(Array.isArray(data) ? data : []);
    //   setUnreadCount(data.filter((msg: FeedbackMessage) => !msg.isRead).length);
    // } catch (error) {
    //   console.error("Feedback fetch error:", error);
    // }
  };

  useEffect(() => {
    if (property?.id) {
      fetchComments();
      fetchFeedback();
    }
  }, [property?.id, quickComment]);

  // ── COMMENT ACTIONS ─────────────────────────────────────────────────────

  const handleAddQuickComment = async () => {
    if (!quickComment.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: quickComment,
          section: commentSection,
          propertyId: property?.id || "",
        }),
      });

      if (!response.ok) throw new Error("Failed to create comment");

      const newComment = await response.json();
      setComments((prev) => [newComment, ...prev]);
      setQuickComment("");
      setShowQuickCommentModal(false);

      toast({ title: "Success", description: "Comment added" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
    }
  };

  const handleResolveComment = async (commentId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/comments/${commentId}/resolve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isResolved: true }),
        },
      );

      if (!response.ok) throw new Error("Failed to resolve");

      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, isResolved: true } : c)),
      );

      toast({ title: "Success", description: "Comment resolved" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not resolve comment",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("Delete this comment?")) return;

    setIsDeletingComment(commentId);
    try {
      const response = await fetch(`${API_BASE_URL}/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Delete failed");

      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast({ title: "Success", description: "Comment deleted" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete comment",
        variant: "destructive",
      });
    } finally {
      setIsDeletingComment(null);
    }
  };

  // ── FEEDBACK ACTIONS ────────────────────────────────────────────────────

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast({
        title: "Error",
        description: "Please write a message",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingFeedback(true);

    try {
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: feedbackMessage,
          priority: feedbackPriority,
          propertyId: property?.id || "",
          senderRole: "supervisor",
        }),
      });

      if (!response.ok) throw new Error("Failed to send feedback");

      const newFeedback = await response.json();
      setFeedbackMessages((prev) => [newFeedback, ...prev]);
      setUnreadCount((prev) => prev + 1);
      setFeedbackMessage("");
      setShowFeedbackModal(false);

      toast({ title: "Success", description: "Feedback sent" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send feedback",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/feedback/${messageId}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setFeedbackMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, isRead: true } : m)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Mark read failed:", err);
    }
  };

  // ── COMMENT VIEW HELPERS ────────────────────────────────────────────────

  const toggleCommentExpand = (commentId: string) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const getTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleRefresh = () => {
    fetchComments();
    fetchFeedback();
    toast({ title: "Refreshed", description: "Data updated" });
  };

  // ── FILTERED COMMENTS ───────────────────────────────────────────────────

  const filteredComments = comments.filter((comment) => {
    // Search filter
    if (commentSearch && comment.message) {
      const searchLower = commentSearch.toLowerCase();
      const messageLower = comment.message.toLowerCase();
      if (!messageLower.includes(searchLower)) return false;
    }

    // Section filter
    if (
      commentSectionFilter !== "all" &&
      comment.section !== commentSectionFilter
    ) {
      return false;
    }

    // Status filter
    if (commentStatusFilter === "resolved" && !comment.isResolved) return false;
    if (commentStatusFilter === "unresolved" && comment.isResolved)
      return false;

    return true;
  });

  // ── RENDER ──────────────────────────────────────────────────────────────
  // Keep the existing valuation logic...
  const [valuation, setValuation] = useState({
    landValue: 0,
    buildingReplacementCost: 0,
    depreciatedValue: 0,
    amenitiesValue: 0,
    totalValue: 0,
    confidenceLevel: 0,
  });
  let amenitiesValue = 0;
  if (property.annexHouseArea)
    amenitiesValue += property.annexHouseArea * property.annexRate;
  if (property.fenceLength)
    amenitiesValue += property.fenceLength * property.fenceRate;
  if (property.gardenArea)
    amenitiesValue += property.gardenArea * property.gardenRate;
  if (property.retainingWallsLength)
    amenitiesValue +=
      property.retainingWallsLength * property.retainingWallRate;
  if (property.entranceGateLength)
    amenitiesValue += property.entranceGateLength * property.entranceGateRate;
  if (property.pavedArea)
    amenitiesValue += property.pavedArea * property.pavedAreaRate;
  if (property.septicTankNumber)
    amenitiesValue += property.septicTankNumber * property.septicTankValue;
  if (property.verandahArea)
    amenitiesValue += property.verandahArea * property.verandahRate;

  useEffect(() => {
    if (isSupervisor && property?.valuationDetails) {
      const buildingReplacementCost =
        Number(property.mainHouseArea) * property.mainHouseRate;
      const depreciatedValue = buildingReplacementCost + amenitiesValue;

      setValuation({
        landValue: property.valuationDetails.landValue || 0,
        buildingReplacementCost:
          Number(property.mainHouseArea) * property.mainHouseRate || 0,
        depreciatedValue:
          Number(property.mainHouseArea) * property.mainHouseRate +
            amenitiesValue || 0,
        amenitiesValue: amenitiesValue || 0,
        totalValue: property.valuationDetails.landValue + depreciatedValue || 0,
        confidenceLevel: 100,
      });
    }
  }, [property]);

  return (
    <div className="space-y-6">
      {/* Valuation Summary */}
      <Card className="bg-white shadow-sm border border-gray-200">
        <CardHeader className="px-6 py-4 border-b">
          <CardTitle className="text-lg font-semibold">
            {isSupervisor ? "Property Valuation" : "Live Valuation"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Land Value</span>
              <span className="font-medium">
                {formatLocalCurrency(property?.landValue)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Building Cost</span>
              <span className="font-medium">
                {formatLocalCurrency(valuation.buildingReplacementCost)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Depreciated Value</span>
              <span className="font-medium">
                {formatLocalCurrency(valuation.depreciatedValue)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Amenities</span>
              <span className="font-medium">
                {formatLocalCurrency(valuation.amenitiesValue)}
              </span>
            </div>
            <hr className="border-gray-200 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">Total Value</span>
              <span className="text-xl font-bold text-primary">
                {formatLocalCurrency(valuation.totalValue || 0)}
              </span>
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500">
            {isSupervisor
              ? "Stored valuation data"
              : "Calculated from current form inputs"}
          </p>
        </CardContent>
      </Card>

      <ValuationResultsSupervisor properties={property} />

      {/* Comments & Feedback Card */}
      <Card className="bg-white shadow-sm border border-gray-200">
        <CardHeader className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold">
              Comments & Feedback
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoadingComments}
            >
              {isLoadingComments ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Refresh"
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Comments Header with Filters and Actions */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium text-gray-900">
                  Comments ({filteredComments.length})
                </h4>
              </div>
              {isSupervisor && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowQuickCommentModal(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                </Button>
              )}
            </div>
            {/* Comments Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              <select
                value={commentSectionFilter}
                onChange={(e) => setCommentSectionFilter(e.target.value)}
                className="px-1 cursor-pointer py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sections</option>
                <option value="valuation">Valuation</option>
                <option value="land">Land</option>
                <option value="building">Building</option>
                <option value="amenities">Amenities</option>
                <option value="general">General</option>
              </select>

              <select
                value={commentStatusFilter}
                onChange={(e) => setCommentStatusFilter(e.target.value as any)}
                className="px-3 py-2 cursor-pointer text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            {/* Comments List */}
            {isLoadingComments ? (
              <div className="text-center py-10">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-sm text-gray-500">
                  Loading comments...
                </p>
              </div>
            ) : filteredComments.length > 0 ? (
              <div
                className={`space-y-4 ${
                  isCommentsExpanded ? "" : "max-h-[500px]"
                } overflow-y-auto pr-2`}
              >
                {filteredComments.map((comment) => {
                  const isOwn = comment.authorRole === "SUPERVISOR";
                  const isExpanded = expandedComments.has(comment.id);
                  const authorName = comment.authorUser
                    ? `${comment.authorUser.firstName} ${
                        comment.authorUser.lastName || ""
                      }`.trim()
                    : "Unknown User";
                  const message = comment.message || "";
                  const shouldTruncate = message.length > 200 && !isExpanded;
                  const displayMessage = shouldTruncate
                    ? message.slice(0, 200) + "..."
                    : message;

                  return (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg border transition-all duration-200 ${
                        comment.isResolved
                          ? "bg-gray-50 border-gray-200"
                          : "bg-blue-50/60 border-blue-200 hover:bg-blue-50"
                      } ${isOwn ? "border-l-4 border-l-indigo-500" : ""} ${
                        hoveredComment === comment.id ? "shadow-md" : ""
                      }`}
                      onMouseEnter={() => setHoveredComment(comment.id)}
                      onMouseLeave={() => setHoveredComment(null)}
                    >
                      <div className="flex justify-between items-start mb-2 text-xs leading-tight text-gray-700 whitespace-pre-wrap">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="h-2 w-2 text-indigo-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {authorName}
                              </p>
                              {isOwn && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                  You
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                  comment.section === "valuation"
                                    ? "bg-purple-100 text-purple-800"
                                    : comment.section === "land"
                                      ? "bg-green-100 text-green-800"
                                      : comment.section === "building"
                                        ? "bg-amber-100 text-amber-800"
                                        : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {comment.section}
                              </span>
                              {comment.isResolved && (
                                <span className="inline-flex items-center gap-1 text-xs px-1 py-0.5 bg-green-100 text-green-800 rounded-full">
                                  <CheckCircle className="h-2 w-2" />
                                  Resolved
                                </span>
                              )}
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-1 w-1 mr-0.5" />
                              {getTimeAgo(comment.createdAt)}
                              {comment.updatedAt !== comment.createdAt && (
                                <span className="ml-2 italic">(edited)</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {isSupervisor && (
                          <div className="flex gap-1 ml-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-2 w-2 hover:bg-blue-100 ml-3  mr-1"
                              onClick={() => setSelectedComment(comment)}
                              title="View full comment"
                            >
                              <Eye className="h-2 w-2 text-gray-600" />
                            </Button>
                            {!comment.isResolved && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-2 w-2 mx-2 hover:bg-green-100"
                                onClick={() => handleResolveComment(comment.id)}
                                title="Mark as resolved"
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-2 w-2 text-red-600 ml-1 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteComment(comment.id)}
                              disabled={isDeletingComment === comment.id}
                              title="Delete comment"
                            >
                              {isDeletingComment === comment.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 ml-3">
                        {message ? (
                          <div className="text-xs leading-tight text-gray-700 whitespace-pre-wrap">
                            {displayMessage}
                            {shouldTruncate && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800 ml-2"
                                onClick={() => toggleCommentExpand(comment.id)}
                              >
                                Read more
                              </Button>
                            )}
                            {isExpanded && message.length > 200 && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-gray-600 hover:text-gray-800 ml-2"
                                onClick={() => toggleCommentExpand(comment.id)}
                              >
                                Show less
                              </Button>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm italic text-gray-500">
                            [No message content]
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
                <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  {commentSearch ||
                  commentSectionFilter !== "all" ||
                  commentStatusFilter !== "all"
                    ? "No comments match your filters"
                    : "No comments yet"}
                </p>
                {isSupervisor && (
                  <Button
                    variant="link"
                    className="mt-2 text-blue-600"
                    onClick={() => setShowQuickCommentModal(true)}
                  >
                    Add your first comment
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── MODALS ───────────────────────────────────────────────────────────── */}

      {/* Comment Detail Modal */}
      {selectedComment && (
        <div className="fixed inset-0 z-50 bg-white overflow-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center shadow-sm">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => setSelectedComment(null)}
                className="mr-4"
              >
                {/* <ChevronLeft className="h-5 w-5" /> */}
                Back
              </Button>
              <h2 className="text-xl font-bold">Comment Details</h2>
            </div>
            <Button variant="ghost" onClick={() => setSelectedComment(null)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedComment.authorUser
                      ? `${selectedComment.authorUser.firstName} ${selectedComment.authorUser.lastName}`
                      : "Unknown User"}
                    {selectedComment.authorRole === "SUPERVISOR" && (
                      <span className="ml-2 text-sm text-indigo-600">
                        (Supervisor)
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedComment.createdAt).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedComment.section === "valuation"
                      ? "bg-purple-100 text-purple-800"
                      : selectedComment.section === "land"
                        ? "bg-green-100 text-green-800"
                        : selectedComment.section === "building"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {selectedComment.section.charAt(0).toUpperCase() +
                    selectedComment.section.slice(1)}
                </span>
                {selectedComment.isResolved ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircle className="h-4 w-4" />
                    Resolved
                    {selectedComment.resolvedAt && (
                      <span className="ml-1 text-xs">
                        on{" "}
                        {new Date(
                          selectedComment.resolvedAt,
                        ).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <AlertCircle className="h-4 w-4" />
                    Unresolved
                  </span>
                )}
                {selectedComment.updatedAt !== selectedComment.createdAt && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    <Edit className="h-4 w-4" />
                    Edited
                  </span>
                )}
              </div>

              <div className="bg-white rounded-lg border p-6 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Message
                </h4>
                <p className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">
                  {selectedComment.message || "[No message content]"}
                </p>
              </div>

              {isSupervisor && (
                <div className="flex gap-3 pt-6 border-t">
                  {!selectedComment.isResolved && (
                    <Button
                      onClick={() => {
                        handleResolveComment(selectedComment.id);
                        setSelectedComment(null);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Resolved
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                    onClick={() => {
                      if (
                        confirm("Are you sure you want to delete this comment?")
                      ) {
                        handleDeleteComment(selectedComment.id);
                        setSelectedComment(null);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Comment
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedComment(null)}
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Send Feedback
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFeedbackModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Priority
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(["P1", "P2", "P3"] as const).map((p) => (
                    <Button
                      key={p}
                      type="button"
                      variant={feedbackPriority === p ? "default" : "outline"}
                      className={
                        feedbackPriority === p
                          ? p === "P1"
                            ? "bg-red-600 hover:bg-red-700"
                            : p === "P2"
                              ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                              : "bg-blue-600 hover:bg-blue-700"
                          : ""
                      }
                      onClick={() => setFeedbackPriority(p)}
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Message
                </label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                  placeholder="Your feedback to the collector..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowFeedbackModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendFeedback}
                  disabled={!feedbackMessage.trim() || isLoadingFeedback}
                >
                  {isLoadingFeedback ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Comment Modal */}
      {showQuickCommentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Add Comment
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowQuickCommentModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Section
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    "valuation",
                    "land",
                    "building",
                    "amenities",
                    "general",
                  ].map((sec) => (
                    <Button
                      key={sec}
                      type="button"
                      variant={commentSection === sec ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCommentSection(sec)}
                    >
                      {sec.charAt(0).toUpperCase() + sec.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Comment
                </label>
                <textarea
                  value={quickComment}
                  onChange={(e) => setQuickComment(e.target.value)}
                  className="w-full h-28 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                  placeholder={`Your comment about ${commentSection}...`}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowQuickCommentModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddQuickComment}
                  disabled={!quickComment.trim()}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
